import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, startCase } from 'lodash'
import classNames from 'classnames'

import { Loader } from '@/components/loaders/Loader'
import {
  COACHING_TASK_TYPE,
  QA_DISPUTE_TASK_TYPE,
  COMPLIANCE_TASK_TYPE,
  QA_MANUAL_SCORE_TASK_TYPE,
  READY_FOR_REVIEW,
} from '@/reducers/tasks/tasks.constants'
import { fetchCall } from '@/reducers/callSearch/callSearch.actions'

import { Metadata } from './Metadata'
import { TaskName } from './TaskName'
import { TaskComments } from '../components/TaskComments'
import { ActiveCoaching } from '../components/ActiveCoaching'
import { CallEvidence } from '../components/CallEvidence'
import { DisputeDrawer } from './DisputeDrawer'
import { ManualScoreDrawer } from './ManualScoreDrawer'
import { TaskDrawerFooter } from './TaskDrawerFooter'

import './TaskDrawer.scss'

export const TaskDrawer = ({
  fetchTasksWithFilters,
  commentsEnabled = false,
  handlePrimaryAction,
  handleSecondaryAction,
  primaryActionButtonText,
  secondaryActionButtonText,
}) => {
  const dispatch = useDispatch()
  const { task, loading } = useSelector((state) => state.tasks)
  const [callDate, setCallDate] = useState(null)
  const channel = task?.evidence?.[0]?.call_type || 'call'
  const agent = `${task?.agent_first_name} ${task?.agent_last_name}`
  const createdBy = task?.created_by
    ? `${task?.created_by_first_name} ${task?.created_by_last_name}`
    : null
  const taskIsOpen = task?.status === READY_FOR_REVIEW
  const canSeeActiveCoaching =
    task.type === COACHING_TASK_TYPE &&
    task.ai_generated &&
    task.ai_coaching_eligible &&
    channel === 'call'

  useEffect(() => {
    if (task?.type !== QA_DISPUTE_TASK_TYPE || isEmpty(task?.evidence)) {
      return
    }

    const callId = task.evidence[0]?.call_id

    if (!callId) {
      return
    }

    dispatch(fetchCall(callId))
      .then((call) => {
        if (call?.metadata?.call_start_time) {
          setCallDate(call.metadata.call_start_time)
        }
      })
      .catch(console.error)
  }, [task?.id])

  const primaryAction = () => handlePrimaryAction(task.id)
  const secondaryAction = taskIsOpen ? () => handleSecondaryAction(task.id) : null

  const getTaskDrawerContent = (taskType) => {
    switch (taskType) {
      case QA_DISPUTE_TASK_TYPE:
        return (
          <DisputeDrawer
            taskIsOpen={taskIsOpen}
            handlePrimaryAction={primaryAction}
            handleSecondaryAction={secondaryAction}
            agent={agent}
            task={task}
          />
        )
      case QA_MANUAL_SCORE_TASK_TYPE:
        return (
          <ManualScoreDrawer
            taskIsOpen={taskIsOpen}
            handlePrimaryAction={primaryAction}
            handleSecondaryAction={secondaryAction}
            task={task}
          />
        )
      default:
        return (
          <TaskDrawerFooter
            handlePrimaryAction={primaryAction}
            handleSecondaryAction={secondaryAction}
            primaryActionButtonText={primaryActionButtonText}
            secondaryActionButtonText={secondaryActionButtonText}
          />
        )
    }
  }

  const getSubheader = (taskType) => {
    switch (taskType) {
      case COACHING_TASK_TYPE:
        return 'Coaching Opportunity'
      case QA_DISPUTE_TASK_TYPE:
      case QA_MANUAL_SCORE_TASK_TYPE:
        return 'Call & Quick Links'
      case COMPLIANCE_TASK_TYPE:
        return 'Exception Reason'
      default:
        return null
    }
  }

  if (isEmpty(task)) {
    return null
  }

  if (loading.task) {
    return (
      <div className="task-drawer">
        <Loader centered />
      </div>
    )
  }

  return (
    <div
      className={classNames('task-drawer', { 'with-comments': commentsEnabled })}
      data-testid="task-drawer"
    >
      <TaskName task={task} />
      <Metadata
        agent={agent}
        createdAt={task.created_at}
        createdBy={createdBy}
        isAiGenerated={task.ai_generated}
        type={task.type}
        status={task.status}
        agentReviewed={task.agent_reviewed}
        callDate={callDate}
      />
      <div className="task-drawer__content">
        {task.description && (
          <>
            <h3>Description</h3>
            <p>{task.description}</p>
          </>
        )}
        {canSeeActiveCoaching && (
          <ActiveCoaching
            agentId={task.agent_id}
            taskId={task.id}
            question={task.name}
            channel={channel}
          />
        )}
        <h3>{getSubheader(task.type)}</h3>
        {!isEmpty(task.evidence) && (
          <CallEvidence
            taskId={task.id}
            organizationId={task.organization_id}
            agentId={task.agent_id}
            taskIsOpen={taskIsOpen}
            fetchTasksWithFilters={fetchTasksWithFilters}
          />
        )}
        {getTaskDrawerContent(task.type)}
      </div>

      {commentsEnabled && (
        <div className="task-drawer__comments">
          <h3>{startCase(task.type)} Notes</h3>
          <TaskComments
            taskId={task.id}
            organizationId={task.organization_id}
            taskType={task.type}
          />
        </div>
      )}
    </div>
  )
}
